import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type {
  MenuItem, MenuGroup, MenuItemGroupMember,
  MenuGroupGroupComplement, MenuItemGroupComplement,
  Location, Business, OnlineStatus, DeliveryZone,
} from '../types';
import { fetchOnlineMenu, fetchLocations, fetchBusiness, fetchWidgetConfig, fetchOnlineStatus, fetchDeliveryZones } from '../api';

interface MenuContextValue {
  items: MenuItem[];
  groups: MenuGroup[];
  memberships: MenuItemGroupMember[];
  groupComplements: MenuGroupGroupComplement[];
  itemComplements: MenuItemGroupComplement[];
  locations: Location[];
  business: Business | null;
  defaultLocationId: number | null;
  onlineStatus: OnlineStatus | null;
  deliveryZones: DeliveryZone[];
  googleApiKey: string | null;
  /** True when Stripe is configured, i.e. card payment is available. */
  cardEnabled: boolean;
  /** True when the card is only reserved at checkout and charged after the
   *  restaurant confirms the order (manual capture). Drives customer messaging. */
  captureOnApproval: boolean;
  widgetTheme: string;
  widgetHeroImage: string | null;
  /** Language codes the widget offers for the menu (incl. the default). */
  widgetLanguages: string[];
  minDeliveryTime: number | null;
  showMinDeliveryTime: boolean;
  /** Minimum lead time (minutes) for scheduled orders; drives the slot picker. */
  delayedMinLeadMinutes: number;
  loading: boolean;
  error: string | null;
  /** Get items that belong to a group */
  getGroupItems: (groupId: number) => MenuItem[];
  /** Get complement groups for an item (item-level + group-level) */
  getItemComplements: (itemId: number) => MenuGroup[];
}

const MenuContext = createContext<MenuContextValue | null>(null);

export function MenuProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [groups, setGroups] = useState<MenuGroup[]>([]);
  const [memberships, setMemberships] = useState<MenuItemGroupMember[]>([]);
  const [groupComplements, setGroupComplements] = useState<MenuGroupGroupComplement[]>([]);
  const [itemComplements, setItemComplements] = useState<MenuItemGroupComplement[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [business, setBusiness] = useState<Business | null>(null);
  const [defaultLocationId, setDefaultLocationId] = useState<number | null>(null);
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatus | null>(null);
  const [deliveryZones, setDeliveryZones] = useState<DeliveryZone[]>([]);
  const [googleApiKey, setGoogleApiKey] = useState<string | null>(null);
  const [cardEnabled, setCardEnabled] = useState(false);
  const [captureOnApproval, setCaptureOnApproval] = useState(false);
  const [widgetTheme, setWidgetTheme] = useState<string>('classic');
  const [widgetHeroImage, setWidgetHeroImage] = useState<string | null>(null);
  const [widgetLanguages, setWidgetLanguages] = useState<string[]>(['hr']);
  const [minDeliveryTime, setMinDeliveryTime] = useState<number | null>(null);
  const [showMinDeliveryTime, setShowMinDeliveryTime] = useState(false);
  const [delayedMinLeadMinutes, setDelayedMinLeadMinutes] = useState<number>(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchOnlineMenu(), fetchLocations(), fetchBusiness(), fetchWidgetConfig(), fetchOnlineStatus(), fetchDeliveryZones()])
      .then(([menu, locs, businesses, widgetConfig, status, zones]) => {
        setItems(menu.items);
        setGroups(menu.groups);
        setMemberships(menu.memberships);
        setGroupComplements(menu.group_complements);
        setItemComplements(menu.item_complements);
        setLocations(locs);
        const biz = businesses[0] ?? null;
        setBusiness(biz);
        setDefaultLocationId(widgetConfig.default_location_id ?? locs[0]?.id ?? null);
        setOnlineStatus(status);
        setDeliveryZones(zones);
        if (widgetConfig.google_maps_key) {
          setGoogleApiKey(widgetConfig.google_maps_key);
        }
        setCardEnabled(!!widgetConfig.stripe_publishable_key);
        setCaptureOnApproval(widgetConfig.payment_capture_on_approval === true);
        if (widgetConfig.widget_theme) {
          setWidgetTheme(widgetConfig.widget_theme);
        }
        if (widgetConfig.widget_hero_image) {
          setWidgetHeroImage(widgetConfig.widget_hero_image);
        }
        if (Array.isArray(widgetConfig.widget_languages) && widgetConfig.widget_languages.length) {
          setWidgetLanguages(widgetConfig.widget_languages);
        } else if (widgetConfig.default_language) {
          setWidgetLanguages([widgetConfig.default_language]);
        }
        setMinDeliveryTime(widgetConfig.min_delivery_time ?? null);
        setShowMinDeliveryTime(widgetConfig.show_min_delivery_time === true);
        setDelayedMinLeadMinutes(widgetConfig.delayed_min_lead_minutes ?? 60);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Poll widget-config + online-status so operator-side changes
  // (min delivery time, paused state) propagate to live widgets.
  useEffect(() => {
    const refresh = () => {
      fetchWidgetConfig()
        .then((cfg) => {
          setMinDeliveryTime(cfg.min_delivery_time ?? null);
          setShowMinDeliveryTime(cfg.show_min_delivery_time === true);
          setDelayedMinLeadMinutes(cfg.delayed_min_lead_minutes ?? 60);
        })
        .catch(() => {});
      fetchOnlineStatus().then(setOnlineStatus).catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(refresh, 30_000);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  function getGroupItems(groupId: number): MenuItem[] {
    const itemIds = memberships
      .filter((m) => m.menu_group_id === groupId)
      .map((m) => m.menu_item_id);
    return items
      .filter((i) => itemIds.includes(i.id))
      .sort((a, b) => (a.sort_id ?? a.id) - (b.sort_id ?? b.id));
  }

  function getItemComplements(itemId: number): MenuGroup[] {
    // Direct item-level complements
    const directGroupIds = itemComplements
      .filter((c) => c.menu_item_id === itemId)
      .map((c) => c.complement_menu_group_id);

    // Group-level complements: find which groups this item belongs to, then their complements
    const itemGroupIds = memberships
      .filter((m) => m.menu_item_id === itemId)
      .map((m) => m.menu_group_id);
    const groupGroupIds = groupComplements
      .filter((c) => itemGroupIds.includes(c.menu_group_id))
      .map((c) => c.complement_menu_group_id);

    const allComplementIds = [...new Set([...directGroupIds, ...groupGroupIds])];
    return groups.filter((g) => allComplementIds.includes(g.id));
  }

  return (
    <MenuContext.Provider
      value={{
        items, groups, memberships, groupComplements, itemComplements,
        locations, business, defaultLocationId, onlineStatus,
        deliveryZones, googleApiKey, cardEnabled, captureOnApproval, widgetTheme, widgetHeroImage, widgetLanguages, minDeliveryTime, showMinDeliveryTime, delayedMinLeadMinutes, loading, error,
        getGroupItems, getItemComplements,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}

export function useMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('useMenu must be used within MenuProvider');
  return ctx;
}
